import { isEqual } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useDynamicFormsBulkActions,
  usePassengersMapBulkActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  errors,
  getPassengerMapStationsSelection,
  IApiError,
  IPassengerDto,
  ISelectListItem,
  LinkStationFormFieldsEnum,
  Log,
  normalizeErrors,
  passengersStationsMapDictionaryDataSelector,
  RydeProcessTitle,
  setsEqual,
  transformApiCrudErrorForToast,
  useOnProcessEnd,
} from "@ryde/shared";

import { yupLinkStationValidationSchema } from "../validations";
import { usePassengerMapSelectedStations } from "./use-passenger-map-selected-stations";

export const usePassengerMapLinkStationPanel = () => {
  const {
    handleStationClick,
    resetSelectedStation,
    passengersIdsAssignSelectedStations,
    passengersMapSelectedStationDictionary,
  } = usePassengerMapSelectedStations();

  const passengersStationsMapDictionaryData = useRecoilValue(
    passengersStationsMapDictionaryDataSelector,
  );

  const { bulkRefetchPassengersStationMapData } = usePassengersMapBulkActions();
  const { bulkUpdate } = useDynamicFormsBulkActions("passenger");

  const { startListening } = useOnProcessEnd({
    entity: "passenger",
    processTitle: RydeProcessTitle.BulkUpdate,
    onProcessEnd: () => {
      bulkRefetchPassengersStationMapData();
    },
  });

  const { t } = useTranslation();

  const { homeAddressList, pickupStationList } = useMemo(() => {
    const homeAddress: ISelectListItem[] = [];
    const pickupStation: ISelectListItem[] = [];

    for (const item of passengersStationsMapDictionaryData.values() ?? []) {
      const { isHomeAddress, addressName, id } = item.stationInfo ?? {};
      if (!id) {
        continue;
      }

      if (isHomeAddress) {
        homeAddress.push({ label: addressName ?? "", value: id });
      } else {
        pickupStation.push({ label: addressName ?? "", value: id });
      }
    }

    return { homeAddressList: homeAddress, pickupStationList: pickupStation };
  }, [passengersStationsMapDictionaryData]);

  const resolver = useYupValidationResolver(
    yupLinkStationValidationSchema,
    "passenger",
  );

  const methods = useForm<{
    homeAddresses: number[];
    pickupStation: number | null;
  }>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      homeAddresses: [],
      pickupStation: null,
    },
    resolver,
  });

  const {
    setValue,
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const homeAddressesValue = watch(LinkStationFormFieldsEnum.homeAddresses);
  const pickupStationValue = watch(LinkStationFormFieldsEnum.pickupStation);

  const isDisabled =
    !isValid || !homeAddressesValue.length || !pickupStationValue;

  const handleClick = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const { pickupStation } = getValues();
    const { assignHomePassengers } = passengersIdsAssignSelectedStations ?? {};

    if (!pickupStation || !assignHomePassengers?.size) {
      return;
    }

    const updatePayload: Partial<IPassengerDto> = {
      sourceStation2Id: pickupStation,
    };

    try {
      const updateResult = await bulkUpdate(
        [...assignHomePassengers],
        updatePayload,
        true,
      );

      if (!updateResult.success) {
        return;
      }

      startListening();
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      Log.warnAndToast(
        transformApiCrudErrorForToast({
          errorsNormalized,
          tFunction: t,
          fallbackTranslationKey: errors.common.errorBulkUpdatingData,
        }),
        error,
        updatePayload,
      );
    }
  });

  // effect syncs map → form
  useEffect(() => {
    const { pickupStation, homeAddresses } = getPassengerMapStationsSelection(
      passengersMapSelectedStationDictionary,
    );

    const currentFormPickup = getValues(
      LinkStationFormFieldsEnum.pickupStation,
    );
    if (pickupStation !== currentFormPickup) {
      setValue(LinkStationFormFieldsEnum.pickupStation, pickupStation, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }

    const currentFormHomes = getValues(LinkStationFormFieldsEnum.homeAddresses);
    if (!isEqual(homeAddresses, currentFormHomes)) {
      setValue(LinkStationFormFieldsEnum.homeAddresses, homeAddresses, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [passengersMapSelectedStationDictionary, getValues, setValue]);

  // effect syncs form → map
  useEffect(() => {
    const { pickupStation: mapPickup, homeAddresses: mapHomes } =
      getPassengerMapStationsSelection(passengersMapSelectedStationDictionary);

    const desiredFormHomes = homeAddressesValue ?? [];
    const desiredFormHomesSet = new Set(desiredFormHomes);
    const mapHomesSet = new Set(mapHomes);

    const isPickupMatch = mapPickup === (pickupStationValue ?? null);
    const isHomesMatch = setsEqual(mapHomesSet, desiredFormHomesSet);

    if (isPickupMatch && isHomesMatch) {
      return;
    }

    // pickup sync
    if (pickupStationValue === null) {
      if (mapPickup !== null) {
        resetSelectedStation({ removePickup: true });
      }
    } else if (mapPickup !== pickupStationValue) {
      const station =
        passengersStationsMapDictionaryData.get(pickupStationValue);
      if (station) {
        handleStationClick(station, false);
      }
    }

    // homes sync (rebuild when mismatch)
    if (!isHomesMatch) {
      if (mapHomes.length) {
        resetSelectedStation({ removeHomeAddress: true });
      }

      for (const id of desiredFormHomes) {
        const station = passengersStationsMapDictionaryData.get(id);
        if (station) {
          handleStationClick(station, false);
        }
      }
    }
    // Omit passengersMapSelectedStationDictionary from deps: this effect syncs form → map
    // only when form values change. Including the map would re-run after every marker
    // click and cause "Maximum update depth exceeded".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    homeAddressesValue,
    pickupStationValue,
    passengersStationsMapDictionaryData,
    handleStationClick,
    resetSelectedStation,
  ]);

  return {
    methods,
    handleClick,
    isDisabled,
    homeAddressList,
    pickupStationList,
  };
};
